import { getBffBaseUrl, getVisitKey, PEOPLE_PATH } from '@/lib/bff/config'

/*
 * BFF client for People & access (VIS-332): the venue users of one tenant,
 * their invitations, the tenant's roles and its access-change record. Every
 * call carries the tenant. The SPA offers each part from the caller's access
 * decision (`users.view`, `access.manage`, `roles.assign`, `roles.define`,
 * `access_changes.view`); the BFF enforces them and decides per row and per
 * role what the caller may do. Errors arrive in the `{ error, holders?,
 * problems? }` envelope.
 */

export type BuiltInRoleId = 'ORGANISATION_ADMIN' | 'SYSTEM_ADMIN'
export const ORGANISATION_ADMIN: BuiltInRoleId = 'ORGANISATION_ADMIN'

/** A built-in role (`name` null; the SPA names it) or a custom role with its tenant-given name. */
export type Role = { id: string; kind: 'builtIn' | 'custom'; name: string | null }

export type PersonRemoval = 'allowed' | 'self' | 'onlyOrganisationAdmin' | 'notPermitted'

export type Person = {
  userId: string
  name: string | null
  email: string | null
  isSelf: boolean
  roles: Role[]
  canChangeRoles: boolean
  removal: PersonRemoval
  onlyOrganisationAdmin: boolean
}

export type Pagination = { page: number; size: number; totalCount: number; totalPages: number }

export type PeoplePage = { data: Person[]; pagination: Pagination }

export type RoleRow = {
  id: string
  kind: 'builtIn' | 'custom'
  name: string | null
  actions: string[]
  holderCount: number
  canAssign: boolean
  canChange: boolean
  includesActionsYouDontHold: boolean
}

export type CustomRole = { id: string; name: string; actions: string[] }

export type Invitation = {
  id: string
  email: string
  name: string | null
  roles: Role[]
  status: 'usable' | 'ended'
  sentAt: string
  validUntil: string
  sentByName: string
}

export type InvitationPage = { data: Invitation[]; pagination: Pagination; timeZone: string }

export type AccessChangeKind =
  | 'invited'
  | 'invitationWithdrawn'
  | 'invitationSentAgain'
  | 'rolesChanged'
  | 'accessRemoved'
  | 'roleCreated'
  | 'roleEdited'
  | 'roleDeleted'

export type AccessChange = {
  id: string
  at: string
  actorName: string | null
  actorKind: 'venueUser' | 'landlord'
  kind: AccessChangeKind
  subject: { email: string | null; name: string | null; roleId: string | null; roleName: string | null }
  /** Role labels (built-in names or custom role names at the time); for a role edit, action ids. */
  change: { added: string[]; removed: string[] }
}

export type AccessChangePage = { data: AccessChange[]; pagination: Pagination; timeZone: string }

export type MemberRoles = { userId: string; roles: string[] }

export type Holder = { userId: string; name: string | null; email: string | null }
export type InputProblem = { field: string; code: string }

/** The page size the People & access lists ask for; the BFF caps `size` at 100. */
export const PEOPLE_PAGE_SIZE = 50
/** The page size used to list every user, for a role's members. */
export const ALL_USERS_PAGE_SIZE = 100

/** Raised for a non-2xx response or a network failure, with the BFF's error code, holders and problems when sent. */
export class PeopleRequestError extends Error {
  readonly status: number
  readonly code: string | null
  readonly holders: Holder[]
  readonly problems: InputProblem[]

  constructor(
    status: number,
    code: string | null,
    { holders = [], problems = [] }: { holders?: Holder[]; problems?: InputProblem[] } = {},
    message = `People request failed (${status})`,
  ) {
    super(message)
    this.name = 'PeopleRequestError'
    this.status = status
    this.code = code
    this.holders = holders
    this.problems = problems
  }
}

function headers(token: string, json = false): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    'x-visit-key': getVisitKey(),
    Accept: 'application/json',
    ...(json ? { 'Content-Type': 'application/json' } : {}),
  }
}

function url(tenantId: string, path: string, query: Record<string, string> = {}) {
  const search = new URLSearchParams({ tenant: tenantId, ...query })
  return `${getBffBaseUrl('mmv')}${PEOPLE_PATH}${path}?${search}`
}

export async function sendRequest(input: string, init: RequestInit): Promise<Response> {
  let response: Response
  try {
    response = await fetch(input, init)
  } catch {
    throw new PeopleRequestError(0, null, {}, 'Network error during a People request')
  }
  if (!response.ok) {
    let code: string | null = null
    let holders: Holder[] = []
    let problems: InputProblem[] = []
    try {
      const body = (await response.json()) as { error?: unknown; holders?: unknown; problems?: unknown }
      if (typeof body?.error === 'string') code = body.error
      if (Array.isArray(body?.holders)) holders = body.holders as Holder[]
      if (Array.isArray(body?.problems)) problems = body.problems as InputProblem[]
    } catch {
      code = null
    }
    throw new PeopleRequestError(response.status, code, { holders, problems })
  }
  return response
}

export async function readJson<T>(response: Response): Promise<T> {
  try {
    return (await response.json()) as T
  } catch {
    throw new PeopleRequestError(response.status, null, {}, 'Malformed People response')
  }
}

const pageQuery = (page: number, size: number) => ({ page: String(page), size: String(size) })

/** One 0-based page of the tenant's venue users, sorted by name. */
export async function listUsers(token: string, tenantId: string, page: number, size = PEOPLE_PAGE_SIZE) {
  const response = await sendRequest(url(tenantId, '/users', { ...pageQuery(page, size), sort: 'name' }), {
    method: 'GET',
    headers: headers(token),
  })
  return readJson<PeoplePage>(response)
}

/** Every venue user of the tenant, page by page. */
export async function listAllUsers(token: string, tenantId: string): Promise<Person[]> {
  const first = await listUsers(token, tenantId, 0, ALL_USERS_PAGE_SIZE)
  const people = [...first.data]
  for (let page = 1; page < first.pagination.totalPages; page += 1) {
    people.push(...(await listUsers(token, tenantId, page, ALL_USERS_PAGE_SIZE)).data)
  }
  return people
}

/** Sets a member's whole role list, by role id. */
export async function changeRoles(token: string, tenantId: string, userId: string, roles: string[]) {
  const response = await sendRequest(url(tenantId, `/users/${encodeURIComponent(userId)}/roles`), {
    method: 'PUT',
    headers: headers(token, true),
    body: JSON.stringify({ roles }),
  })
  return readJson<MemberRoles>(response)
}

export async function removeAccess(token: string, tenantId: string, userId: string): Promise<void> {
  await sendRequest(url(tenantId, `/users/${encodeURIComponent(userId)}`), { method: 'DELETE', headers: headers(token) })
}

export async function listInvitations(token: string, tenantId: string, page: number, size = PEOPLE_PAGE_SIZE) {
  const response = await sendRequest(url(tenantId, '/invitations', pageQuery(page, size)), {
    method: 'GET',
    headers: headers(token),
  })
  return readJson<InvitationPage>(response)
}

export async function invite(token: string, tenantId: string, request: { email: string; name?: string; roles: string[] }) {
  const response = await sendRequest(url(tenantId, '/invitations'), {
    method: 'POST',
    headers: headers(token, true),
    body: JSON.stringify(request),
  })
  return readJson<Invitation>(response)
}

export async function withdrawInvitation(token: string, tenantId: string, invitationId: string): Promise<void> {
  await sendRequest(url(tenantId, `/invitations/${encodeURIComponent(invitationId)}/withdraw`), {
    method: 'POST',
    headers: headers(token),
  })
}

export async function sendInvitationAgain(token: string, tenantId: string, invitationId: string) {
  const response = await sendRequest(url(tenantId, `/invitations/${encodeURIComponent(invitationId)}/send-again`), {
    method: 'POST',
    headers: headers(token),
  })
  return readJson<Invitation>(response)
}

export async function listRoles(token: string, tenantId: string) {
  const response = await sendRequest(url(tenantId, '/roles'), { method: 'GET', headers: headers(token) })
  return readJson<{ data: RoleRow[] }>(response)
}

export async function createRole(token: string, tenantId: string, request: { name: string; actions: string[] }) {
  const response = await sendRequest(url(tenantId, '/roles'), {
    method: 'POST',
    headers: headers(token, true),
    body: JSON.stringify(request),
  })
  return readJson<CustomRole>(response)
}

export async function updateRole(token: string, tenantId: string, roleId: string, request: { name: string; actions: string[] }) {
  const response = await sendRequest(url(tenantId, `/roles/${encodeURIComponent(roleId)}`), {
    method: 'PUT',
    headers: headers(token, true),
    body: JSON.stringify(request),
  })
  return readJson<CustomRole>(response)
}

export async function deleteRole(token: string, tenantId: string, roleId: string): Promise<void> {
  await sendRequest(url(tenantId, `/roles/${encodeURIComponent(roleId)}`), { method: 'DELETE', headers: headers(token) })
}

/** Sets who holds a role, by user id; returns only the members whose roles changed. */
export async function setRoleMembers(token: string, tenantId: string, roleId: string, userIds: string[]) {
  const response = await sendRequest(url(tenantId, `/roles/${encodeURIComponent(roleId)}/members`), {
    method: 'PUT',
    headers: headers(token, true),
    body: JSON.stringify({ userIds }),
  })
  return readJson<{ changed: MemberRoles[] }>(response)
}

/** One 0-based page of the tenant's access changes, newest first. */
export async function listAccessChanges(token: string, tenantId: string, page: number, size = PEOPLE_PAGE_SIZE) {
  const response = await sendRequest(url(tenantId, '/changes', pageQuery(page, size)), {
    method: 'GET',
    headers: headers(token),
  })
  return readJson<AccessChangePage>(response)
}
